import { useCallback } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pagesService } from '../services/pagesService';
import { notificationService } from '../services/notificationService';
import { invalidateAfterAlertChange, queryKeys } from '../api/queryClient';
import { useAuth } from './useAuth';
import { useDismissableError } from './useDismissableError';

const ALERTS_LIMIT = 20;

function patchItem(items, next) {
  if (!next?.id) return items;
  return items.map((item) => (item.id === next.id ? { ...item, ...next } : item));
}

function patchInfinitePages(data, patchFn) {
  if (!data?.pages) return data;
  return {
    ...data,
    pages: data.pages.map((page, index) => patchFn(page, index)),
  };
}

export function useInfiniteAlerts(enabled = true, limit = ALERTS_LIMIT, filters = {}) {
  const { applyViewer, user, unreadAlertCount } = useAuth();
  const queryClient = useQueryClient();
  const q = filters.q || undefined;
  const kind = filters.kind && filters.kind !== 'all' ? filters.kind : undefined;
  const queryKey = queryKeys.alerts(limit, user?.id ?? null, { q, kind });

  const query = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }) => {
      const data = await pagesService.alerts({
        limit,
        cursor: pageParam,
        q,
        kind,
      });
      if (data?.viewer) applyViewer(data.viewer);
      return data;
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage?.meta?.nextCursor ?? undefined,
    enabled,
    staleTime: 30_000,
  });

  const updateCacheItem = useCallback(
    (next) => {
      queryClient.setQueryData(queryKey, (prev) =>
        patchInfinitePages(prev, (page) => ({
          ...page,
          items: patchItem(page.items ?? [], next),
        })),
      );
    },
    [queryClient, queryKey],
  );

  const bumpUnread = useCallback(
    (item, next) => {
      const becameRead = !item.read && next?.read;
      if (!becameRead) return;
      const nextUnread = Math.max(0, unreadAlertCount - 1);
      queryClient.setQueryData(queryKey, (prev) =>
        patchInfinitePages(prev, (page, index) => ({
          ...page,
          meta:
            index === 0 && page.meta
              ? { ...page.meta, unreadCount: nextUnread }
              : page.meta,
        })),
      );
      applyViewer({ unreadAlertCount: nextUnread });
    },
    [applyViewer, queryClient, queryKey, unreadAlertCount],
  );

  const open = useCallback(
    async (item) => {
      const next = item.engagement?.openedByMe
        ? await notificationService.click(item.id)
        : await notificationService.open(item.id);
      updateCacheItem(next);
      bumpUnread(item, next);
      void invalidateAfterAlertChange(queryClient);
      return next;
    },
    [bumpUnread, queryClient, updateCacheItem],
  );

  const react = useCallback(
    async (item, emoji) => {
      // Caller may already pass null to clear; otherwise toggle.
      const nextReaction =
        emoji === null || emoji === undefined
          ? null
          : item.engagement?.myReaction === emoji
            ? null
            : emoji;
      const next = await notificationService.react(item.id, nextReaction);
      updateCacheItem(next);
      return next;
    },
    [updateCacheItem],
  );

  const share = useCallback(
    async (item) => {
      const next = await notificationService.share(item.id);
      updateCacheItem(next);
      return next;
    },
    [updateCacheItem],
  );

  const invokeAction = useCallback(
    async (item, action) => {
      const res = await notificationService.invokeAction(item.id, action.id);
      if (res?.notification) {
        updateCacheItem(res.notification);
        bumpUnread(item, res.notification);
      }
      void invalidateAfterAlertChange(queryClient);
      return res;
    },
    [bumpUnread, queryClient, updateCacheItem],
  );

  const markAllMutation = useMutation({
    mutationFn: () => notificationService.markAllRead(),
    onSuccess: () => {
      queryClient.setQueryData(queryKey, (prev) =>
        patchInfinitePages(prev, (page) => ({
          ...page,
          items: (page.items ?? []).map((item) => ({ ...item, read: true })),
          viewer: page.viewer
            ? { ...page.viewer, unreadAlertCount: 0 }
            : page.viewer,
          meta: page.meta
            ? { ...page.meta, unreadCount: 0 }
            : page.meta,
        })),
      );
      applyViewer({ unreadAlertCount: 0 });
    },
  });

  const items = query.data?.pages?.flatMap((page) => page?.items ?? []) ?? [];
  const firstPage = query.data?.pages?.[0];
  const { error, setError } = useDismissableError(query.error);

  return {
    items,
    unreadCount:
      firstPage?.meta?.unreadCount ??
      firstPage?.viewer?.unreadAlertCount ??
      items.filter((i) => !i.read).length,
    loading: query.isLoading || (query.isFetching && items.length === 0),
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    error,
    setError,
    open,
    react,
    share,
    invokeAction,
    markAllRead: () => markAllMutation.mutateAsync(),
    reload: () => query.refetch(),
  };
}
